import { useState } from "react"
import { CatalogProductEntity } from "../Objects/ProductsAndCatalogItems/CatalogProductEntity"
import { EEnableStatusType } from "../Enum/EEnableStatusType"
import { categoryMemory, promotionsMemory } from "../Objects/Memory/Memory"
import { AddtionalEntity } from "../Objects/Addtional/AddtionalEntity"
import { SelectionEntity } from "../Objects/Selection/SelectionEntity"
import { EProductOrderModeType } from "../Enum/EProductOrderModeType"

export const useCreateCatalogProductEntity = (hasNeedFulfillmentDefault: boolean = false) => {
  const getDefaultNonComboCategoryId = () =>
    categoryMemory.listData.find((category) => category.name.trim().toLowerCase() !== "combos")?.id ?? categoryMemory.firstId()

  const [promotionsIds, setPromotionsIds] = useState<number[]>([])

  const [name, setName] = useState("")

  const [description, setDescription] = useState("")

  const [coast, setCoast] = useState("")

  const [status, setStatus] = useState<EEnableStatusType>(EEnableStatusType.Active)

  const [categoryId, setCategoryId] = useState(getDefaultNonComboCategoryId())

  const [catalogProductEntity, setCatalogProductEntity] = useState<CatalogProductEntity | null>(null)

  const [additionalsAllowed, setAdditionalsAllowed] = useState<AddtionalEntity[]>([])

  const [freeAdditionalsAllowed, setFreeAdditionalsAllowed] = useState<AddtionalEntity[]>([])
  const [allowedSelections, setAllowedSelections] = useState<SelectionEntity[]>([])

  const [freeAddtionalsLimit, setFreeAddtionalsLimit] = useState("0")

  const [tags, setTags] = useState<string[]>([])

  const [imageAssetIds, setImageAssetIds] = useState<number[]>([])

  const [imageUrls, setImageUrls] = useState<string[]>([])

  const [imagesChanged, setImagesChanged] = useState(false)

  const [hasStockControl, setHasStockControl] = useState(false)

  const [stockQuantity, setStockQuantity] = useState("")

  const [hasNeedFulfillment, setHasNeedFulfillment] = useState(hasNeedFulfillmentDefault)
  
  const [hasPreparationTime, setHasPreparationTime] = useState(false)
  
  const [preparationTimeMinutes, setPreparationTimeMinutes] = useState("")

  const [productOrderMode, setProductOrderMode] = useState<EProductOrderModeType>(EProductOrderModeType.ImmediateOnly)

  const [reservationAllowedDays, setReservationAllowedDays] = useState<number[]>([])

  const [reservationAllowedTimes, setReservationAllowedTimes] = useState<string[]>([])

  const handleCategoryId = (e: number) => setCategoryId(e)
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)
  const handleCoastChange = (e: React.ChangeEvent<HTMLInputElement>) => setCoast(e.target.value)
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setStatus(e.target.selectedIndex as EEnableStatusType)
  const handleFreeAddtionalsLimitChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFreeAddtionalsLimit(e.target.value)
  const handleHasStockControlChange = (value: boolean) => setHasStockControl(value)
  const handleHasNeedFulfillmentChange = (value: boolean) => setHasNeedFulfillment(value)
  const handleHasPreparationTimeChange = (value: boolean) => setHasPreparationTime(value)
  const handlePreparationTimeMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setPreparationTimeMinutes(e.target.value)
  const handleStockQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => setStockQuantity(e.target.value)
  const handleProductOrderModeChange = (value: EProductOrderModeType) => setProductOrderMode(value)

  const handleInitialCatalogProductEntity = (catalogProductEntity: CatalogProductEntity | null | undefined) => {
    if (catalogProductEntity) {
      setPromotionsIds(catalogProductEntity.promotionsIds)
      setCatalogProductEntity(catalogProductEntity)
      setCategoryId(catalogProductEntity.categoryId || getDefaultNonComboCategoryId())
      setName(catalogProductEntity.name)
      setDescription(catalogProductEntity.description)
      setCoast(catalogProductEntity.valueBase.toString())
      setStatus(catalogProductEntity.status)
      setAdditionalsAllowed(catalogProductEntity.allowedAddtionals)
      setFreeAdditionalsAllowed(catalogProductEntity.freeAllowedAddtionals ?? [])
      setAllowedSelections(catalogProductEntity.allowedSelections ?? [])
      setFreeAddtionalsLimit((catalogProductEntity.freeAddtionalsLimit ?? 0).toString())
      setTags(catalogProductEntity.tags ?? [])
      setImageAssetIds(catalogProductEntity.imageAssetIds ?? [])
      setImageUrls(catalogProductEntity.imageUrls ?? (catalogProductEntity.iconUrl ? [catalogProductEntity.iconUrl] : []))
      setImagesChanged(catalogProductEntity.id === -1)
      setHasStockControl(catalogProductEntity.hasStockControl)
      setStockQuantity(catalogProductEntity.stockQuantity.toString())
      setHasNeedFulfillment(catalogProductEntity.needFulfillment)
      setHasPreparationTime(catalogProductEntity.hasPreparationTime)
      setPreparationTimeMinutes((catalogProductEntity.preparationTimeMinutes ?? 0).toString())
      setProductOrderMode(catalogProductEntity.productOrderMode ?? EProductOrderModeType.ImmediateOnly)
      setReservationAllowedDays(catalogProductEntity.reservationAllowedDays ?? [])
      setReservationAllowedTimes((catalogProductEntity.reservationAllowedTimes ?? []).map((time) => time.toShortTime()))

      return
    }

    setPromotionsIds([])
    setCatalogProductEntity(null)
    setCategoryId(getDefaultNonComboCategoryId())
    setName("")
    setDescription("")
    setCoast("")
    setStatus(EEnableStatusType.Active)
    setAdditionalsAllowed([])
    setFreeAdditionalsAllowed([])
    setAllowedSelections([])
    setFreeAddtionalsLimit("0")
    setTags([])
    setImageAssetIds([])
    setImageUrls([])
    setImagesChanged(false)
    setHasStockControl(false)
    setStockQuantity("")
    setHasNeedFulfillment(hasNeedFulfillmentDefault)
    setHasPreparationTime(false)
    setPreparationTimeMinutes("")
    setProductOrderMode(EProductOrderModeType.ImmediateOnly)
    setReservationAllowedDays([])
    setReservationAllowedTimes([])
  }

  const handleCatalogProductEntity = () => {
    return new CatalogProductEntity({
      id: catalogProductEntity?.id,
      status: status,
      valueBase: Number(coast),
      name: name,
      categoryId: categoryId,
      category: categoryMemory.findById(categoryId),
      description: description,
      promotionsIds: promotionsIds,
      promotions: promotionsMemory.getByIds(promotionsIds),
      allowedAddtionals: additionalsAllowed,
      freeAllowedAddtionals: freeAdditionalsAllowed,
      allowedSelections: allowedSelections,
      freeAddtionalsLimit: Number(freeAddtionalsLimit || 0),
      tags: tags,
      imageAssetIds,
      imageUrls,
      imagesChanged,
      hasStockControl: hasStockControl,
      stockQuantity: Number(stockQuantity || 0),
      needFulfillment: hasNeedFulfillment,
      hasPreparationTime: hasPreparationTime,
      preparationTimeMinutes: hasPreparationTime ? Number(preparationTimeMinutes || 0) : 0,
      productOrderMode: productOrderMode,
      reservationAllowedDays: reservationAllowedDays,
      reservationAllowedTimes: reservationAllowedTimes,
    })
  }

  return {
    name,
    description,
    status,
    coast,
    categoryId,
    promotionsIds,
    additionalsAllowed,
    freeAdditionalsAllowed,
    allowedSelections,
    freeAddtionalsLimit,
    tags,
    imageAssetIds,
    imageUrls,
    imagesChanged,
    hasStockControl,
    stockQuantity,
    hasNeedFulfillment,
    hasPreparationTime,
    preparationTimeMinutes,
    productOrderMode,
    reservationAllowedDays,
    reservationAllowedTimes,
    setPromotionsIds,
    setTags,
    setImageAssetIds,
    setImageUrls,
    setImagesChanged,
    setAdditionalsAllowed,
    setFreeAdditionalsAllowed,
    setAllowedSelections,
    handleNameChange,
    handleCoastChange,
    handleCategoryId,
    handleDescriptionChange,
    handleStatusChange,
    handleFreeAddtionalsLimitChange,
    handleHasStockControlChange,
    handleHasNeedFulfillmentChange,
    handleHasPreparationTimeChange,
    handlePreparationTimeMinutesChange,
    handleStockQuantityChange,
    handleProductOrderModeChange,
    setReservationAllowedDays,
    setReservationAllowedTimes,
    handleCatalogProductEntity,
    handleInitialCatalogProductEntity,
  }
}
